import { Plus } from "@phosphor-icons/react"
import { useCallback, useMemo, useRef, useState } from "react"

import { Button } from "~/components/reusable/Button"
import { Input } from "~/components/reusable/Input"
import { Loading } from "~/components/reusable/Loading"
import { Separator } from "~/components/reusable/Separator"
import {
  useCreateTag,
  useDeleteTag,
  useGetTags,
  useGetTagsOnSkuIds,
  useUpdateTag,
  useUpdateTagsOnSkus,
} from "~/hooks/useTags"
import { trackEvent } from "~/hooks/useTrackingService"
import { useVariantsByIds } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"

import { TagItem, type TagStatus } from "./TagItem"

type Props = {
  variantIds: string[]
  variantsOverride?: Pick<IVariantSummary, "variantId" | "skuIds">[]
  onClose?: () => void
}

export const EditSkuTags = ({ variantIds, variantsOverride, onClose }: Props) => {
  const { data: tags, isPending: isLoadingTags } = useGetTags()
  const storeVariants = useVariantsByIds(variantIds)
  // Pre-resolved variants for callers whose SKU store isn't hydrated (e.g. Product Management's inbox tab); defaults to the store lookup.
  const variants = variantsOverride ?? storeVariants
  const { mutate: createTag } = useCreateTag()
  const { mutate: deleteTag } = useDeleteTag()
  const { mutate: updateTag } = useUpdateTag()
  const { mutate: updateTagsOnSkus, isPending: isUpdatingTags } = useUpdateTagsOnSkus()
  const { data: tagsOnSkus, isLoading: isLoadingTagsOnSkus } = useGetTagsOnSkuIds(
    variants.flatMap((variant) => variant.skuIds ?? []),
  )
  const skuIds = variants.flatMap((variant) => variant.skuIds ?? [])

  const [pendingTagIdsToAdd, setPendingTagIdsToAdd] = useState<number[]>([])
  const [pendingTagIdsToRemove, setPendingTagIdsToRemove] = useState<number[]>([])
  const [newTagName, setNewTagName] = useState<string>("")
  const [hasOverflow, setHasOverflow] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const isAtBottomRef = useRef(false)

  const checkOverflow = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      const hasVerticalOverflow = element.scrollHeight > element.clientHeight
      setHasOverflow(hasVerticalOverflow)
      // Check if already at bottom
      const threshold = 5
      const isScrolledToBottom =
        element.scrollTop + element.clientHeight >= element.scrollHeight - threshold
      isAtBottomRef.current = isScrolledToBottom
      setIsAtBottom(isScrolledToBottom)
    }
  }, [])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget
    const threshold = 5
    const isScrolledToBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - threshold

    // Only update state if the bottom state actually changed
    if (isScrolledToBottom !== isAtBottomRef.current) {
      isAtBottomRef.current = isScrolledToBottom
      setIsAtBottom(isScrolledToBottom)
    }
  }, [])

  const scrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }

      if (element) {
        checkOverflow(element)

        const resizeObserver = new ResizeObserver(() => {
          checkOverflow(element)
        })
        resizeObserver.observe(element)
        resizeObserverRef.current = resizeObserver
      } else {
        setHasOverflow(false)
      }
    },
    [checkOverflow],
  )

  const tagStatuses = useMemo<TagStatus[]>(() => {
    if (!tags) return []

    const selectedVariantIdSet = new Set(variantIds ?? [])

    const tagsBySkuId = new Map<string, number[]>()
    for (const tagOnSku of tagsOnSkus ?? []) {
      const existing = tagsBySkuId.get(tagOnSku.skuId)
      if (existing) {
        existing.push(tagOnSku.tagId)
      } else {
        tagsBySkuId.set(tagOnSku.skuId, [tagOnSku.tagId])
      }
    }

    const variantIdsByTagId = {} as Record<number, Set<string>>

    for (const variant of variants ?? []) {
      if (!selectedVariantIdSet.has(variant.variantId)) continue
      const tagIdsForVariant = new Set<number>()

      for (const skuId of variant.skuIds ?? []) {
        const tagIdsOnSku = tagsBySkuId.get(skuId) ?? []
        for (const tagId of tagIdsOnSku) {
          tagIdsForVariant.add(tagId)
        }
      }

      for (const tagId of tagIdsForVariant) {
        if (!variantIdsByTagId[tagId]) {
          variantIdsByTagId[tagId] = new Set()
        }
        variantIdsByTagId[tagId]!.add(variant.variantId)
      }
    }

    const totalSelectedVariants = selectedVariantIdSet.size

    return tags.map((tag) => {
      const initialCount = variantIdsByTagId[tag.id]?.size ?? 0
      const isPendingAdd = pendingTagIdsToAdd.includes(tag.id)
      const isPendingRemove = pendingTagIdsToRemove.includes(tag.id)

      let currentCount = initialCount
      if (isPendingAdd) {
        currentCount = totalSelectedVariants
      } else if (isPendingRemove) {
        currentCount = 0
      }

      return {
        tagId: tag.id,
        name: tag.name,
        currentCount,
        initialCount,
        allVariantsHaveTag: currentCount === totalSelectedVariants,
        noVariantsHaveTag: currentCount === 0,
        skuCount: Number(tag.tagOnSkuCount),
      }
    })
  }, [tagsOnSkus, tags, variants, variantIds, pendingTagIdsToAdd, pendingTagIdsToRemove])

  const handleCreateTag = (name: string) => {
    if (!name.trim()) return
    createTag(
      { name },
      {
        onSuccess: (res) => {
          trackEvent("Tag Created", { Name: name })
          setNewTagName("")
          setPendingTagIdsToAdd((prev) => [...prev, res.id])
        },
      },
    )
  }

  const handleToggleAddTag = (tagId: number) => {
    setPendingTagIdsToRemove((prev) => prev.filter((id) => id !== tagId))
    setPendingTagIdsToAdd((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    )
  }

  const handleToggleRemoveTag = (tagId: number) => {
    setPendingTagIdsToAdd((prev) => prev.filter((id) => id !== tagId))
    setPendingTagIdsToRemove((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    )
  }

  const handleDeleteTag = (tagId: number) => {
    deleteTag({ tagId })
    setPendingTagIdsToAdd((prev) => prev.filter((id) => id !== tagId))
    setPendingTagIdsToRemove((prev) => prev.filter((id) => id !== tagId))
  }

  const handleRenameTag = (tagId: number, newName: string) => {
    updateTag({ tagId, name: newName })
  }

  const netTagIdsToAdd = pendingTagIdsToAdd.filter((id) => !pendingTagIdsToRemove.includes(id))
  const netTagIdsToRemove = pendingTagIdsToRemove.filter((id) => !pendingTagIdsToAdd.includes(id))

  const hasPendingChanges = useMemo(() => {
    let result = false
    if (netTagIdsToAdd.length === 0 && netTagIdsToRemove.length === 0) {
      result = false
    } else {
      const buildTagSkuSet = (tagSkuPairs: Array<{ tagId: number; skuId: string }>) => {
        return new Set(tagSkuPairs.map((pair) => `${pair.tagId}-${pair.skuId}`))
      }

      const skuIdSet = new Set(skuIds)
      const initialTagSkuPairs = (tagsOnSkus ?? [])
        .filter((tagOnSku) => skuIdSet.has(tagOnSku.skuId))
        .map((tagOnSku) => ({ tagId: tagOnSku.tagId, skuId: tagOnSku.skuId }))

      const initialSet = buildTagSkuSet(initialTagSkuPairs)
      const finalSet = new Set(initialSet)

      netTagIdsToRemove.forEach((tagId) => {
        skuIds.forEach((skuId) => finalSet.delete(`${tagId}-${skuId}`))
      })

      netTagIdsToAdd.forEach((tagId) => {
        skuIds.forEach((skuId) => finalSet.add(`${tagId}-${skuId}`))
      })

      if (initialSet.size !== finalSet.size) {
        result = true
      } else {
        for (const item of initialSet) {
          if (!finalSet.has(item)) {
            result = true
            break
          }
        }
      }
    }
    return result
  }, [tagsOnSkus, skuIds, netTagIdsToAdd, netTagIdsToRemove])

  const filteredTagStatuses = useMemo(() => {
    if (!newTagName.trim()) return tagStatuses

    const searchTerm = newTagName.toLowerCase()
    return tagStatuses.filter((tag) => tag.name.toLowerCase().includes(searchTerm))
  }, [tagStatuses, newTagName])

  const exactTagExists = useMemo(() => {
    if (!newTagName.trim()) return false
    const searchTerm = newTagName.toLowerCase()
    return tagStatuses.some((tag) => tag.name.toLowerCase() === searchTerm)
  }, [tagStatuses, newTagName])

  const appliedTags = useMemo(
    () =>
      filteredTagStatuses
        .filter((tag) => tag.currentCount > 0)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [filteredTagStatuses],
  )

  const unappliedTags = useMemo(
    () =>
      filteredTagStatuses
        .filter((tag) => tag.currentCount === 0)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [filteredTagStatuses],
  )

  const handleApplyChanges = () => {
    if (netTagIdsToAdd.length === 0 && netTagIdsToRemove.length === 0) return

    updateTagsOnSkus(
      {
        skuIds,
        tagIdsToRemove: netTagIdsToRemove,
        tagIdsToAdd: netTagIdsToAdd,
        variantCount: variantIds.length,
      },
      {
        onSuccess: () => {
          trackEvent("Tags Updated", {
            "Number of SKUs": skuIds.length,
            "Number of Tags Added": netTagIdsToAdd.length,
            "Number of Tags Removed": netTagIdsToRemove.length,
          })
          setPendingTagIdsToAdd([])
          setPendingTagIdsToRemove([])
          onClose?.()
        },
      },
    )
  }

  if (isLoadingTags || isLoadingTagsOnSkus) {
    return (
      <div className="flex items-center justify-center min-w-[400px] min-h-[300px]">
        <Loading />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 min-w-[400px]">
      <div className="flex flex-col gap-2">
        <Input
          value={newTagName}
          containerClassName="w-full text-md text-fg"
          variant="invisible"
          onChange={(e) => setNewTagName(e.target.value)}
          placeholder="Change or add tags..."
        />
        {newTagName.trim() && !exactTagExists && (
          <>
            <div className="-mx-6">
              <Separator orientation="horizontal" />
            </div>
            <div className="flex items-center justify-start ">
              <span
                className="flex items-center gap-3 p-2 hover:bg-subtle rounded-md cursor-pointer w-full"
                onClick={() => handleCreateTag(newTagName)}
              >
                <Plus />
                <p className="text-md text-fg">Create & add new tag: &ldquo;{newTagName}&rdquo;</p>
              </span>
            </div>
          </>
        )}
      </div>
      <div className="-mx-6 mb-1">
        <Separator orientation="horizontal" />
      </div>
      <div className="flex flex-col relative">
        <div
          ref={scrollContainerRef}
          className="relative max-h-[300px] min-h-[300px] overflow-y-auto -ml-2 pr-2"
          onScroll={handleScroll}
          style={
            hasOverflow && !isAtBottom
              ? {
                  maskImage:
                    "linear-gradient(to bottom, black calc(100% - 86px), transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, black calc(100% - 86px), transparent 100%)",
                }
              : undefined
          }
        >
          <div className="flex flex-col pb-2">
            {appliedTags.length > 0 && (
              <>
                <p className="text-sm text-fg-subtle">Applied tags</p>
                {appliedTags.map((tag) => (
                  <TagItem
                    key={tag.tagId}
                    tag={tag}
                    totalVariantCount={variantIds.length}
                    onToggleAdd={handleToggleAddTag}
                    onToggleRemove={handleToggleRemoveTag}
                    onDelete={handleDeleteTag}
                    onRename={handleRenameTag}
                  />
                ))}
              </>
            )}
            {unappliedTags.length > 0 && (
              <>
                <p
                  className={`text-sm text-fg-subtle mb-1 ${appliedTags.length > 0 ? "mt-3" : ""}`}
                >
                  Unapplied tags
                </p>
                {unappliedTags.map((tag) => (
                  <TagItem
                    key={tag.tagId}
                    tag={tag}
                    totalVariantCount={variantIds.length}
                    onToggleAdd={handleToggleAddTag}
                    onToggleRemove={handleToggleRemoveTag}
                    onDelete={handleDeleteTag}
                    onRename={handleRenameTag}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="-mx-6 mb-1 -mt-1">
        <Separator orientation="horizontal" />
      </div>
      <div className="-m-4 p-4 flex flex-row gap-3 justify-end bg-subtle rounded-b-[16px]">
        <Button type="button" variant="neutral" onClick={() => onClose?.()}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          requires="edit"
          onClick={handleApplyChanges}
          disabled={!hasPendingChanges || isUpdatingTags}
          loading={isUpdatingTags}
        >
          Save & Apply
        </Button>
      </div>
    </div>
  )
}
